import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Upload, Loader2, Check } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/lib/toast';
import { UserAvatar } from '@/components/profile/UserAvatar';
import { useQueryClient } from '@tanstack/react-query';
import { AvatarCropDialog } from '@/components/profile/AvatarCropDialog';
import { GlassModal } from '@/components/ui/glass-modal';
import { STORAGE_BUCKETS } from '@/lib/storageBuckets';

interface AvatarUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentAvatarUrl: string | null;
  username: string | null;
  email?: string | null;
  onSuccess?: (newUrl: string) => void;
}

const MAX_SIZE = 5 * 1024 * 1024;

function validateAvatarFile(file: File): string | null {
  if (file.size > MAX_SIZE) return 'Image must be less than 5MB';
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  if (!allowedTypes.includes(file.type)) return 'Only JPG, PNG, GIF, and WebP images are allowed';
  const fileExt = file.name.split('.').pop()?.toLowerCase();
  const validExtensions: Record<string, string[]> = {
    'image/jpeg': ['jpg', 'jpeg'],
    'image/png': ['png'],
    'image/gif': ['gif'],
    'image/webp': ['webp']
  };
  const expectedExtensions = validExtensions[file.type];
  if (!expectedExtensions || !fileExt || !expectedExtensions.includes(fileExt)) {
    return "File extension doesn't match image type";
  }
  return null;
}

function generateAvatarUrl(publicUrl: string): string {
  const url = new URL(publicUrl);
  url.searchParams.set('t', Date.now().toString());
  return url.toString();
}

export function AvatarUploadDialog({
  open,
  onOpenChange,
  currentAvatarUrl,
  username,
  email,
  onSuccess
}: AvatarUploadDialogProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [, setSelectedFileType] = useState<string | null>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    if (!target.files || target.files.length === 0 || !user?.id) {
      target.value = '';
      return;
    }

    let file = target.files[0];
    try {
      const { convertHeicIfNeeded } = await import('@/lib/heicUtils');
      file = await convertHeicIfNeeded(file);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not read that photo');
      target.value = '';
      return;
    }

    const validationError = validateAvatarFile(file);
    if (validationError) {
      toast.error(validationError);
      target.value = '';
      return;
    }

    setSelectedFileType(file.type);
    const objectUrl = URL.createObjectURL(file);
    setImageToCrop(objectUrl);
    setCropDialogOpen(true);
    target.value = '';
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    if (!user?.id) return;
    if (imageToCrop) {
      URL.revokeObjectURL(imageToCrop);
      setImageToCrop(null);
    }
    const outputContentType = croppedBlob.type || 'image/jpeg';
    const fileExt = outputContentType === 'image/png' ? 'png' : 'jpg';

    const croppedObjectUrl = URL.createObjectURL(croppedBlob);
    setPreviewUrl(croppedObjectUrl);

    queryClient.setQueryData(['profile', user.id], (old: any) =>
      old ? { ...old, avatar_url: croppedObjectUrl } : old
    );
    queryClient.setQueriesData({ queryKey: ['seller-profile'] }, (old: any) =>
      old ? { ...old, avatar_url: croppedObjectUrl } : old
    );

    onSuccess?.(croppedObjectUrl);
    setUploadSuccess(true);
    setTimeout(() => onOpenChange(false), 300);

    try {
      setUploading(true);
      const { resizeImage, IMMUTABLE_CACHE_CONTROL } = await import('@/lib/imageResizeUtils');
      const croppedFile = new File([croppedBlob], `avatar.${fileExt}`, { type: outputContentType });
      const compressed = await resizeImage(croppedFile, { preset: 'avatar' });
      const finalExt = (compressed.name.split('.').pop() || fileExt).toLowerCase();
      const filePath = `${user.id}/avatar.${finalExt}`;
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, compressed, { upsert: true, contentType: compressed.type, cacheControl: IMMUTABLE_CACHE_CONTROL });
      if (uploadError) throw uploadError;
      const { data } = supabase.storage.from(STORAGE_BUCKETS.avatars).getPublicUrl(filePath);
      if (!data?.publicUrl) throw new Error('Failed to get avatar URL');
      const avatarUrlWithTimestamp = generateAvatarUrl(data.publicUrl);
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: avatarUrlWithTimestamp })
        .eq('user_id', user.id);
      if (updateError) {
        await supabase.storage.from(STORAGE_BUCKETS.avatars).remove([filePath]);
        throw new Error('Failed to save avatar. Please try again.');
      }
      queryClient.setQueryData(['profile', user.id], (old: any) =>
        old ? { ...old, avatar_url: avatarUrlWithTimestamp } : old
      );
      queryClient.setQueriesData({ queryKey: ['seller-profile'] }, (old: any) =>
        old ? { ...old, avatar_url: avatarUrlWithTimestamp } : old
      );
      // Force every consumer (BottomNav, UserMenu, storefront card, etc.) to refetch
      // with the new cache-busted URL instead of relying on scattered optimistic patches.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['profile'] }),
        queryClient.invalidateQueries({ queryKey: ['profile', user.id] }),
        queryClient.invalidateQueries({ queryKey: ['seller-profile'] }),
        queryClient.invalidateQueries({ queryKey: ['public-profile'] }),
      ]);
      onSuccess?.(avatarUrlWithTimestamp);
    } catch (error: any) {
      console.error('Avatar upload error:', error);
      toast.error(error.message || 'Failed to upload. Please try again.');
    } finally {
      setUploading(false);
      setUploadSuccess(false);
      setPreviewUrl(null);
      setSelectedFileType(null);
    }
  };

  const handleCropDialogClose = () => {
    setCropDialogOpen(false);
    if (imageToCrop) {
      URL.revokeObjectURL(imageToCrop);
      setImageToCrop(null);
    }
    setSelectedFileType(null);
  };

  return (
    <>
      <GlassModal
        open={open}
        onOpenChange={onOpenChange}
        title="Change Profile Picture"
        desktopMaxWidth={400}
      >
        <h2 className="text-lg font-semibold">Change Profile Picture</h2>

        <div className="flex flex-col items-center gap-6 py-4">
          <div className="relative">
            <UserAvatar
              avatarUrl={previewUrl || currentAvatarUrl}
              username={username}
              email={email}
              size="xl"
              className="h-24 w-24"
            />
            {uploadSuccess && (
              <div className="absolute inset-0 flex items-center justify-center bg-success/80 rounded-full">
                <Check className="h-10 w-10 text-white" />
              </div>
            )}
          </div>

          {uploading && (
            <div className="w-full space-y-2">
              <Progress value={uploadProgress} className="h-2" />
              <p className="text-xs text-center text-muted-foreground">
                Uploading... {uploadProgress}%
              </p>
            </div>
          )}

          <div className="w-full">
            <input
              type="file"
              id="avatar-dialog-upload"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,.heic,.heif"
              onChange={handleFileSelect}
              className="hidden"
              disabled={uploading}
            />
            <Button
              type="button"
              variant="outline"
              disabled={uploading}
              onClick={() => document.getElementById('avatar-dialog-upload')?.click()}
              className="w-full"
            >
              {uploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Choose Image
                </>
              )}
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-2">
              JPG, PNG, GIF, or WebP. Max 5MB.
            </p>
          </div>
        </div>
      </GlassModal>

      <AvatarCropDialog
        open={cropDialogOpen}
        imageSrc={imageToCrop}
        onClose={handleCropDialogClose}
        onCropComplete={handleCropComplete}
      />
    </>
  );
}
